'use strict';

class AperteRangeFilter {
  constructor(container) {
    this.container = container;
    this.unit = container.dataset.unit || '';
    this.paramName = container.dataset.paramName || '';

    const raw = (container.dataset.values || '').split(',');
    const nums = raw.map(v => parseFloat(v.trim())).filter(v => !isNaN(v));
    this.values = [...new Set(nums)].sort((a, b) => a - b);

    if (this.values.length < 2) return;

    // Read active range from URL params
    const urlParams = new URLSearchParams(window.location.search);
    const active = urlParams.getAll(this.paramName).map(v => parseFloat(v)).filter(v => !isNaN(v));

    this.loVal = active.length ? Math.min(...active) : this.values[0];
    this.hiVal = active.length ? Math.max(...active) : this.values[this.values.length - 1];

    this.build();
  }

  idxOf(val) {
    const exact = this.values.indexOf(val);
    if (exact >= 0) return exact;
    // nearest
    let best = 0;
    this.values.forEach((v, i) => { if (Math.abs(v - val) < Math.abs(this.values[best] - val)) best = i; });
    return best;
  }

  pct(idx) { return (idx / (this.values.length - 1)) * 100; }

  isFullRange(lo, hi) { return lo === 0 && hi === this.values.length - 1; }

  build() {
    const loI = this.idxOf(this.loVal);
    const hiI = this.idxOf(this.hiVal);
    const last = this.values.length - 1;
    this.container.innerHTML = `
      <div class="aperte-rf__wrap">
        <div class="aperte-rf__track">
          <div class="aperte-rf__fill" style="left:${this.pct(loI)}%;right:${100 - this.pct(hiI)}%"></div>
        </div>
        <input class="aperte-rf__thumb aperte-rf__thumb--lo" type="range" min="0" max="${last}" value="${loI}" step="1">
        <input class="aperte-rf__thumb aperte-rf__thumb--hi" type="range" min="0" max="${last}" value="${hiI}" step="1">
      </div>
      <div class="aperte-rf__labels">
        <span class="aperte-rf__lo">${this.values[loI]}${this.unit}</span>
        <span class="aperte-rf__hi">${this.values[hiI]}${this.unit}</span>
      </div>
    `;

    this.thumbLo = this.container.querySelector('.aperte-rf__thumb--lo');
    this.thumbHi = this.container.querySelector('.aperte-rf__thumb--hi');
    this.fill    = this.container.querySelector('.aperte-rf__fill');
    this.labelLo = this.container.querySelector('.aperte-rf__lo');
    this.labelHi = this.container.querySelector('.aperte-rf__hi');

    this._lastMoved = 'hi';
    this.thumbLo.addEventListener('input', () => { this._lastMoved = 'lo'; this.slide(); });
    this.thumbHi.addEventListener('input', () => { this._lastMoved = 'hi'; this.slide(); });
    this.thumbLo.addEventListener('change', () => this.commit());
    this.thumbHi.addEventListener('change', () => this.commit());

    this.updateZIndex(loI, hiI);
  }

  slide() {
    let lo = parseInt(this.thumbLo.value);
    let hi = parseInt(this.thumbHi.value);
    if (lo > hi) {
      if (this._lastMoved === 'lo') { lo = hi; this.thumbLo.value = lo; }
      else                          { hi = lo; this.thumbHi.value = hi; }
    }
    this.fill.style.left  = this.pct(lo) + '%';
    this.fill.style.right = (100 - this.pct(hi)) + '%';
    this.labelLo.textContent = this.values[lo] + this.unit;
    this.labelHi.textContent = this.values[hi] + this.unit;
    this.updateZIndex(lo, hi);
  }

  updateZIndex(lo, hi) {
    const atMax = lo >= this.values.length - 1;
    this.thumbLo.style.zIndex = atMax ? '4' : '3';
    this.thumbHi.style.zIndex = atMax ? '3' : '4';
  }

  commit() {
    const lo = parseInt(this.thumbLo.value);
    const hi = parseInt(this.thumbHi.value);
    const full = this.isFullRange(lo, hi);

    // Build new search params from current URL
    const current = new URLSearchParams(window.location.search);
    // Remove this filter's existing params
    current.delete(this.paramName);

    // Add range values only if not full range
    if (!full && this.paramName) {
      const minVal = this.values[lo];
      const maxVal = this.values[hi];
      this.values
        .filter(v => v >= minVal && v <= maxVal)
        .forEach(v => current.append(this.paramName, String(v)));
    }

    // Remove page param when filter changes
    current.delete('page');

    const searchStr = current.toString();

    // Use Dawn's FacetFiltersForm if available, fallback to navigation
    if (typeof FacetFiltersForm !== 'undefined' && typeof FacetFiltersForm.renderPage === 'function') {
      FacetFiltersForm.renderPage(searchStr);
    } else {
      const form = document.getElementById('FacetFiltersForm');
      const facetEl = form && form.closest('facet-filters-form');
      if (facetEl && typeof facetEl.renderPage === 'function') {
        facetEl.renderPage(searchStr);
      } else {
        window.location.search = searchStr;
      }
    }
  }
}

class ApertePriceFilter {
  constructor(container) {
    this.container = container;
    this.rangeMax   = parseFloat(container.dataset.rangeMax) || 0;
    this.activeMin  = parseFloat(container.dataset.activeMin) || 0;
    this.activeMax  = parseFloat(container.dataset.activeMax) || this.rangeMax;
    this.paramMin   = container.dataset.paramMin || '';
    this.paramMax   = container.dataset.paramMax || '';
    this.currency   = container.dataset.currency || '';

    if (this.rangeMax <= 0) return;

    this.step = this.calcStep(this.rangeMax);
    this.build();
  }

  calcStep(max) {
    if (max <= 500)   return 10;
    if (max <= 2000)  return 50;
    if (max <= 10000) return 100;
    return 500;
  }

  fmt(val) {
    return new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 0 }).format(val) + ' ' + this.currency;
  }

  pct(val) { return (val / this.rangeMax) * 100; }

  isFullRange(lo, hi) { return lo <= 0 && hi >= this.rangeMax; }

  build() {
    const lo = this.activeMin;
    const hi = this.activeMax;

    this.container.innerHTML = `
      <div class="aperte-rf__wrap">
        <div class="aperte-rf__track">
          <div class="aperte-rf__fill" style="left:${this.pct(lo)}%;right:${100 - this.pct(hi)}%"></div>
        </div>
        <input class="aperte-rf__thumb aperte-rf__thumb--lo" type="range"
               min="0" max="${this.rangeMax}" value="${lo}" step="${this.step}">
        <input class="aperte-rf__thumb aperte-rf__thumb--hi" type="range"
               min="0" max="${this.rangeMax}" value="${hi}" step="${this.step}">
      </div>
      <div class="aperte-rf__price-inputs">
        <div class="aperte-rf__price-field">
          <input class="aperte-rf__price-text aperte-rf__price-text--lo" type="number"
                 min="0" max="${this.rangeMax}" value="${lo}" step="${this.step}">
          <span class="aperte-rf__price-currency">${this.currency}</span>
        </div>
        <span class="aperte-rf__price-sep">–</span>
        <div class="aperte-rf__price-field">
          <input class="aperte-rf__price-text aperte-rf__price-text--hi" type="number"
                 min="0" max="${this.rangeMax}" value="${hi}" step="${this.step}">
          <span class="aperte-rf__price-currency">${this.currency}</span>
        </div>
      </div>
    `;

    this.thumbLo  = this.container.querySelector('.aperte-rf__thumb--lo');
    this.thumbHi  = this.container.querySelector('.aperte-rf__thumb--hi');
    this.fill     = this.container.querySelector('.aperte-rf__fill');
    this.inputLo  = this.container.querySelector('.aperte-rf__price-text--lo');
    this.inputHi  = this.container.querySelector('.aperte-rf__price-text--hi');

    this._lastMoved = 'hi';
    this.thumbLo.addEventListener('input', () => { this._lastMoved = 'lo'; this.slide(); });
    this.thumbHi.addEventListener('input', () => { this._lastMoved = 'hi'; this.slide(); });
    this.thumbLo.addEventListener('change', () => this.commit());
    this.thumbHi.addEventListener('change', () => this.commit());

    this.inputLo.addEventListener('change', () => this.onTextInput('lo'));
    this.inputHi.addEventListener('change', () => this.onTextInput('hi'));
    this.inputLo.addEventListener('keydown', e => { if (e.key === 'Enter') this.onTextInput('lo'); });
    this.inputHi.addEventListener('keydown', e => { if (e.key === 'Enter') this.onTextInput('hi'); });

    this.updateZIndex(lo, hi);
  }

  slide() {
    let lo = parseFloat(this.thumbLo.value);
    let hi = parseFloat(this.thumbHi.value);
    if (lo > hi) {
      if (this._lastMoved === 'lo') { lo = hi; this.thumbLo.value = lo; }
      else                          { hi = lo; this.thumbHi.value = hi; }
    }
    this.fill.style.left  = this.pct(lo) + '%';
    this.fill.style.right = (100 - this.pct(hi)) + '%';
    this.inputLo.value = lo;
    this.inputHi.value = hi;
    this.updateZIndex(lo, hi);
  }

  onTextInput(which) {
    let lo = parseFloat(this.inputLo.value) || 0;
    let hi = parseFloat(this.inputHi.value) || this.rangeMax;
    lo = Math.max(0, Math.min(lo, this.rangeMax));
    hi = Math.max(0, Math.min(hi, this.rangeMax));
    if (lo > hi) { if (which === 'lo') lo = hi; else hi = lo; }
    this.inputLo.value    = lo;
    this.inputHi.value    = hi;
    this.thumbLo.value    = lo;
    this.thumbHi.value    = hi;
    this.fill.style.left  = this.pct(lo) + '%';
    this.fill.style.right = (100 - this.pct(hi)) + '%';
    this.updateZIndex(lo, hi);
    this.commit();
  }

  updateZIndex(lo, hi) {
    const atMax = lo >= this.rangeMax;
    this.thumbLo.style.zIndex = atMax ? '4' : '3';
    this.thumbHi.style.zIndex = atMax ? '3' : '4';
  }

  commit() {
    const lo = parseFloat(this.thumbLo.value);
    const hi = parseFloat(this.thumbHi.value);
    const full = this.isFullRange(lo, hi);

    const current = new URLSearchParams(window.location.search);
    current.delete(this.paramMin);
    current.delete(this.paramMax);
    current.delete('page');

    if (!full) {
      if (lo > 0)              current.set(this.paramMin, lo.toString());
      if (hi < this.rangeMax)  current.set(this.paramMax, hi.toString());
    }

    const searchStr = current.toString();
    if (typeof FacetFiltersForm !== 'undefined' && typeof FacetFiltersForm.renderPage === 'function') {
      FacetFiltersForm.renderPage(searchStr);
    } else {
      const form = document.getElementById('FacetFiltersForm');
      const facetEl = form && form.closest('facet-filters-form');
      if (facetEl && typeof facetEl.renderPage === 'function') {
        facetEl.renderPage(searchStr);
      } else {
        window.location.search = searchStr;
      }
    }
  }
}

function initRangeFilters(root) {
  (root || document).querySelectorAll('.aperte-range-filter:not([data-rf-init])').forEach(el => {
    el.dataset.rfInit = 'true';
    if (el.dataset.type === 'price') {
      new ApertePriceFilter(el);
    } else {
      new AperteRangeFilter(el);
    }
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initRangeFilters();
  const wrap = document.getElementById('main-collection-filters');
  if (wrap) {
    new MutationObserver(() => initRangeFilters()).observe(wrap, { childList: true, subtree: true });
  }
});
